import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { SET_VALUE_OPTIONS } from '@/sections/address/components/AddressForm/Autocomplete/constants';
import type { AddressFormValues } from '@/sections/address/types/AddressFormValues';

export const useInputValueSync = (
  inputRef: React.MutableRefObject<HTMLInputElement | null>
) => {
  const { setValue, watch } = useFormContext<AddressFormValues>();

  /**
   * use a MutationObserver to detect when the autocomplete service directly modifies the input's value attribute.
   * When detected, it updates the form's streetName field to maintain consistency between the DOM and form state.
   */
  useEffect(() => {
    if (inputRef.current) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          const isAttributeMutation = mutation.type === 'attributes';
          const isValueMutation = mutation.attributeName === 'value';

          if (!isAttributeMutation || !isValueMutation) return;

          const streetName = watch('streetName');
          const newValue = inputRef?.current?.value;
          if (newValue && newValue !== streetName) {
            setValue('streetName', newValue, SET_VALUE_OPTIONS);
          }
        });
      });

      observer.observe(inputRef.current, { attributes: true });

      return () => observer.disconnect();
    }
  }, [inputRef, setValue, watch]);
};
